/**
 * Copy-Editor der Report-Strecken — dieselben Bausteine wie der PLX-Editor
 * (TinyMCE, Platzhalter-Legende, Spam-Score, Vorschau, Testversand), aber ohne
 * die Kampagnen-Hülle: kein Sendetermin, keine Client-Auswahl, keine
 * Serien-Semantik. Für eine Flow-Copy ist nichts davon gültig.
 *
 * Gespeichert wird über den BESTEHENDEN Endpunkt
 * `POST /api/plx-mailings/campaigns/:id/emails`. Das ist Absicht: dort sitzen
 * der Betreff-Dedup gegen die effektive Serie, der Löschschutz für
 * flow-referenzierte Zeilen und die Diversitäts-Bestätigung. Ein eigener
 * Speicherpfad wäre eine zweite Tür zu demselben Zustand — genau die Klasse
 * Fehler, an der diese Strecke schon einmal hing.
 *
 * ⚠ Der Upsert LÖSCHT jede Mailing-Nummer der Kampagne, die nicht mitgeschickt
 * wird. Deshalb werden immer ALLE Mails der Kampagne geladen und
 * zurückgeschrieben, auch wenn nur eine bearbeitet wird: eine Strecke kann per
 * Mailing-Id auch auf eine gewachsene Bestandskampagne zeigen, und die verlöre
 * sonst ihre übrigen Mails. Gelesen wird dafür UNMITTELBAR VOR dem Speichern
 * neu, nicht der Stand vom Öffnen — der Dialog steht beliebig lange offen, und
 * eine in der Zwischenzeit hinzugefügte Mail würde sonst mitgelöscht.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import TinyMCEEditor, { TinyMCEEditorRef } from '../../components/TinyMCEEditor';
import SpamScorePanel from '../../components/SpamScorePanel';
import EmailPreviewDialog from '../../components/EmailPreviewDialog';
import TestEmailDialog from '../../components/TestEmailDialog';
import PlaceholderLegend from '../plx-mailings/PlaceholderLegend';
import { api } from '../../helpers/apiUtils';

interface MailRow {
  id: number;
  mailing_number: number;
  subject: string;
  from_name: string | null;
  preview_text: string | null;
  html_body: string;
  scheduled_datetime: string;
  selected_clients: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Kampagne, in der die Copy liegt. */
  campaignId: number;
  /** Die zu bearbeitende Zeile aus plx_mailing_emails. */
  mailingId: number;
  /** Nur für die Überschrift. */
  flowName: string;
  position: number;
  onSaved: () => void;
}

// Eine Flow-Copy geht ausschliesslich über FT raus — die Welcome-Serie kennt
// keinen Client-Fanout. Vorschau und Testversand deshalb fest auf 'ft'.
const FT_CLIENT = [{ code: 'ft', name: 'Finanztrends' }];

const ReportFlowCopyEditor = ({
  open,
  onClose,
  campaignId,
  mailingId,
  flowName,
  position,
  onSaved,
}: Props) => {
  const [mails, setMails] = useState<MailRow[]>([]);
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [fromName, setFromName] = useState('');
  const [html, setHtml] = useState('');
  const [productCode, setProductCode] = useState('');
  const [landingUrl, setLandingUrl] = useState('');
  const [mode, setMode] = useState<'wysiwyg' | 'code'>('wysiwyg');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const editorRef = useRef<TinyMCEEditorRef | null>(null);
  const monacoRef = useRef<any>(null);

  const current = mails.find((m) => m.id === mailingId) ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/plx-mailings/campaigns/${campaignId}`);
      const campaign = res.data?.data;
      const rows: MailRow[] = campaign?.emails ?? [];
      const row = rows.find((m) => Number(m.id) === Number(mailingId));
      if (!row) {
        setError(`Mailing ${mailingId} liegt nicht in Kampagne ${campaignId}.`);
        setMails([]);
        return;
      }
      setMails(rows);
      setSubject(row.subject ?? '');
      setPreviewText(row.preview_text ?? '');
      setFromName(row.from_name ?? '');
      setHtml(row.html_body ?? '');
      setProductCode(campaign?.product_code ?? '');
      setLandingUrl(campaign?.landing_url ?? '');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [campaignId, mailingId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  /** Der Monaco-Puffer ist die Wahrheit, solange der Code-Modus aktiv ist. */
  const currentHtml = () =>
    mode === 'code' && monacoRef.current ? monacoRef.current.getValue() : html;

  /**
   * Schreibt die bearbeitete Zeile zurück und schickt alle übrigen unverändert
   * mit (siehe Kopfkommentar). `confirm_diversity` reicht die weiche
   * Bestätigung des 409-Pfades durch — ohne sie bliebe der Riegel eine
   * Sackgasse.
   */
  const save = async (confirmDiversity = false): Promise<boolean> => {
    if (!current) return false;
    if (!subject.trim()) {
      setError('Betreff darf nicht leer sein.');
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      // Den Stand FRISCH lesen, nicht den beim Öffnen geladenen benutzen.
      //
      // Der Upsert löscht jede Mailing-Nummer, die nicht mitkommt. Der Dialog
      // steht aber beliebig lange offen, und wer in der Zwischenzeit im
      // PLX-Editor eine Mail an dieselbe Kampagne hängt, verlöre sie beim
      // nächsten Speichern hier — der Flow-Löschschutz greift nur für
      // flow-referenzierte Zeilen, eine fremde Geschwisterzeile fällt durch.
      // Das schliesst das Fenster auf die Dauer dieses Round-Trips.
      const fresh = await api.get(`/api/plx-mailings/campaigns/${campaignId}`);
      const freshRows: MailRow[] = fresh.data?.data?.emails ?? [];
      if (!freshRows.some((m) => Number(m.id) === Number(mailingId))) {
        setError(
          `Mailing ${mailingId} liegt nicht mehr in Kampagne ${campaignId} — nicht gespeichert. Bitte den Dialog schliessen und neu öffnen.`,
        );
        return false;
      }
      setMails(freshRows);

      const payload = freshRows.map((m) =>
        Number(m.id) === Number(mailingId)
          ? {
              ...m,
              subject: subject.trim(),
              preview_text: previewText || null,
              from_name: fromName || null,
              html_body: currentHtml(),
            }
          : m,
      );
      const res = await api.post(`/api/plx-mailings/campaigns/${campaignId}/emails`, {
        emails: payload,
        ...(confirmDiversity ? { confirm_diversity: true } : {}),
      });
      if (!res.data?.success) throw new Error(res.data?.error || 'Speichern fehlgeschlagen');
      setInfo('Gespeichert.');
      onSaved();
      return true;
    } catch (err: any) {
      const body = err?.response?.data;
      if (err?.response?.status === 409 && body?.requires_confirmation) {
        const proceed = window.confirm(
          `${body.error}\n\nTrotzdem speichern?`,
        );
        setSaving(false);
        if (proceed) return save(true);
        return false;
      }
      setError(body?.error || err?.message || 'Speichern fehlgeschlagen');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    if (!current) return;
    // Erst speichern, sonst zeigt die Vorschau den Stand vor der Bearbeitung —
    // der Endpunkt rendert aus der DB, nicht aus dem Puffer.
    if (!(await save())) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const res = await api.get(
        `/api/plx-mailings/campaigns/${campaignId}/preview?client=ft&email_number=${current.mailing_number}`,
      );
      setPreviewHtml(res.data?.data?.html ?? res.data?.data?.html_body ?? '');
    } catch (err: any) {
      setPreviewHtml('');
      setError(err?.response?.data?.error || 'Vorschau fehlgeschlagen');
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTest = async (
    testEmails: string[],
  ): Promise<{ success: boolean; message?: string }> => {
    if (!current) return { success: false, message: 'Keine Copy geladen' };
    if (!(await save())) return { success: false, message: 'Speichern fehlgeschlagen' };
    try {
      const res = await api.post(`/api/plx-mailings/campaigns/${campaignId}/send-test`, {
        test_emails: testEmails,
        client_codes: ['ft'],
        email_number: current.mailing_number,
      });
      const results = res.data?.data?.results ?? [];
      const failed = results.filter((r: any) => !r.success);
      if (failed.length > 0) {
        return {
          success: false,
          message: failed.map((r: any) => `${r.clientCode}: ${r.error}`).join('; '),
        };
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, message: err?.response?.data?.error || err?.message };
    }
  };

  const insert = (placeholder: string) => {
    if (mode === 'code' && monacoRef.current) {
      const editor = monacoRef.current;
      editor.executeEdits('insert', [
        { range: editor.getSelection(), text: placeholder, forceMoveMarkers: true },
      ]);
    } else if (editorRef.current) {
      editorRef.current.insertContent(placeholder);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <span>
              Copy bearbeiten — {flowName}, Position {position}
            </span>
            {productCode && <Chip size="small" label={productCode} />}
            <Chip size="small" variant="outlined" label={`Mailing ${mailingId}`} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Verkaufsmail auf {landingUrl || 'die Bestellseite'} — kein Sendetermin, der Versand
            hängt am Anmeldezeitpunkt des Report-Abonnenten.
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {info && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
              {info}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : !current ? null : (
            <>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Betreff"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Preheader"
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Absendername"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Stack>
              </Stack>

              <PlaceholderLegend onInsert={insert} />

              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={mode}
                  onChange={(_e, value) => {
                    if (!value) return;
                    // Beim Verlassen des Code-Modus den Monaco-Puffer
                    // uebernehmen, sonst faellt die Bearbeitung auf den Stand
                    // vor dem Umschalten zurueck.
                    if (mode === 'code' && monacoRef.current) setHtml(monacoRef.current.getValue());
                    setMode(value);
                  }}
                >
                  <ToggleButton value="wysiwyg">Editor</ToggleButton>
                  <ToggleButton value="code">HTML</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {mode === 'wysiwyg' ? (
                  <TinyMCEEditor
                    ref={editorRef}
                    value={html}
                    onChange={(content) => setHtml(content)}
                  />
                ) : (
                  <Editor
                    height="500px"
                    defaultLanguage="html"
                    value={html}
                    onChange={(value) => setHtml(value || '')}
                    onMount={(editor) => {
                      monacoRef.current = editor;
                    }}
                    options={{ minimap: { enabled: false }, wordWrap: 'on' }}
                  />
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <SpamScorePanel
                  editorType="plx_campaign"
                  referenceId={current.mailing_number}
                  campaignId={campaignId}
                  subject={subject}
                  preheader={previewText}
                  body={html}
                  fromName={fromName}
                />
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Schliessen</Button>
          <Button onClick={openPreview} disabled={saving || loading || !current}>
            Vorschau
          </Button>
          <Button onClick={() => setTestOpen(true)} disabled={saving || loading || !current}>
            Testmail
          </Button>
          <Button
            variant="contained"
            onClick={() => save()}
            disabled={saving || loading || !current}
          >
            {saving ? 'Speichert …' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <EmailPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        previewHtml={previewHtml}
        subject={subject}
        clients={FT_CLIENT}
        selectedClient="ft"
        onClientChange={() => undefined}
        loading={previewLoading}
      />

      <TestEmailDialog
        open={testOpen}
        onClose={() => setTestOpen(false)}
        onSend={sendTest}
        availableClients={FT_CLIENT.map((c) => ({
          code: c.code,
          name: c.name,
          esp_type: 'iterable' as const,
        }))}
        defaultSelectedClients={['ft']}
        title="Testmail der Report-Copy"
      />
    </>
  );
};

export default ReportFlowCopyEditor;
